import { useEffect } from 'react';
import { absoluteUrl, app, brand, socialAssets } from '@ircf/config';

/**
 * Document metadata for a single-page application.
 *
 * A static-hosted SPA has one `index.html`, so title, description, canonical URL
 * and Open Graph tags must be written imperatively per route. Crawlers that
 * execute JavaScript pick these up; the pre-render step in the build writes the
 * same values into per-route HTML files for those that do not.
 *
 * Tags created here are tagged with `data-managed="seo"` and removed on cleanup,
 * so navigating away never leaves a previous route's metadata behind.
 */

export interface SeoInput {
  title: string;
  description: string;
  /** Route path, used to build the canonical URL. */
  path: string;
  /** Absolute or app-relative social preview image. */
  imageUrl?: string;
  /** `website` for pages, `article` for individual results. */
  ogType?: 'website' | 'article' | 'profile';
  /** Excluded from indexing - every authenticated route sets this. */
  noIndex?: boolean;
  /** JSON-LD, included only when every fact in it is genuinely known. */
  structuredData?: Record<string, unknown> | null;
}

const MANAGED = 'data-managed';
const MANAGED_VALUE = 'seo';

function upsertMeta(attribute: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    element.setAttribute(MANAGED, MANAGED_VALUE);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute(MANAGED, MANAGED_VALUE);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function setStructuredData(data: Record<string, unknown> | null): void {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED}="${MANAGED_VALUE}"]`)
    .forEach((node) => node.remove());

  if (!data) return;

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(MANAGED, MANAGED_VALUE);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/** Applies metadata for the current route. */
export function useSeo(input: SeoInput): void {
  const {
    title,
    description,
    path,
    imageUrl,
    ogType = 'website',
    noIndex = false,
    structuredData = null,
  } = input;

  useEffect(() => {
    const fullTitle = title.includes(brand.businessName)
      ? title
      : `${title} | ${brand.businessName}`;
    const canonical = absoluteUrl(path);
    // `absoluteUrl` joins onto the configured public URL, so `assetUrl` must not
    // be applied as well or a compatibility mount could be duplicated.
    const image = imageUrl
      ? imageUrl.startsWith('http')
        ? imageUrl
        : absoluteUrl(imageUrl)
      : absoluteUrl(socialAssets.masterOpenGraph);

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', canonical);

    upsertMeta(
      'name',
      'robots',
      noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    );

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:site_name', brand.businessName);
    upsertMeta('property', 'og:locale', app.locale.replace('-', '_'));

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    setStructuredData(structuredData);

    return () => setStructuredData(null);
  }, [title, description, path, imageUrl, ogType, noIndex, structuredData]);
}
